import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useLanguage } from '../contexts/LanguageContext'
import { requestApi } from '../lib/adminApi'
import { clearAdminToken, resolveAdminToken, saveAdminToken } from '../lib/utils'
import { History, Insights, People, ReceiptLong, Settings, Shield, Tune, ViewQuilt } from '@mui/icons-material'

type AccessState = 'checking' | 'unauthenticated' | 'denied' | 'granted'

interface AdminLayoutProps {
  title: string
  description?: string
  actions?: React.ReactNode
  children: React.ReactNode
}

type AdminProfile = {
  id: string
  username?: string | null
  phone?: string | null
  email?: string | null
}

type AdminLoginResponse = {
  token: string
  admin: AdminProfile
}

type AdminSessionResponse = {
  admin: AdminProfile
}

type AdminAccessContextValue = {
  access: AccessState
  isGranted: boolean
}

const AdminAccessContext = createContext<AdminAccessContextValue>({ access: 'checking', isGranted: false })

export const useAdminAccess = () => useContext(AdminAccessContext)

export const AdminLayout: React.FC<AdminLayoutProps> = ({ title, description, actions, children }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useLanguage()
  const [access, setAccess] = useState<AccessState>('checking')
  const [adminName, setAdminName] = useState<string>('')
  const [adminHint, setAdminHint] = useState<string>('')
  const [loginUsername, setLoginUsername] = useState<string>('')
  const [loginPassword, setLoginPassword] = useState<string>('')
  const [loginError, setLoginError] = useState<string>('')
  const [loginLoading, setLoginLoading] = useState<boolean>(false)

  const navItems = useMemo(() => ([
    { key: 'overview', label: t('admin.nav.overview'), path: '/admin', icon: <ViewQuilt fontSize="small" /> },
    { key: 'ai', label: t('admin.nav.aiChannels'), path: '/admin/ai-channels', icon: <Tune fontSize="small" /> },
    { key: 'ai-monitor', label: t('admin.nav.aiMonitor'), path: '/admin/ai-channels/monitor', icon: <Insights fontSize="small" /> },
    { key: 'billing', label: t('admin.nav.billing'), path: '/admin/billing', icon: <ReceiptLong fontSize="small" /> },
    { key: 'consumption', label: t('admin.nav.consumption'), path: '/admin/consumption', icon: <ReceiptLong fontSize="small" /> },
    { key: 'users', label: t('admin.nav.users'), path: '/admin/users', icon: <People fontSize="small" /> },
    { key: 'logs', label: t('admin.nav.logs'), path: '/admin/logs', icon: <History fontSize="small" /> },
    { key: 'settings', label: t('admin.nav.settings'), path: '/admin/settings', icon: <Settings fontSize="small" /> },
  ]), [t])

  useEffect(() => {
    const token = resolveAdminToken()
    if (!token) {
      setAccess('unauthenticated')
      return
    }

    requestApi<AdminSessionResponse>('/api/v1/admin/auth/session')
      .then((payload) => {
        const admin = payload?.admin
        const name = admin?.username || admin?.phone || admin?.email || ''
        setAdminName(name)
        if (admin?.phone) {
          setAdminHint(admin.phone)
        } else if (admin?.email) {
          setAdminHint(admin.email)
        } else {
          setAdminHint('')
        }
        setAccess('granted')
      })
      .catch(() => {
        clearAdminToken()
        setAccess('unauthenticated')
      })
  }, [])

  const handleLoginSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!loginUsername || !loginPassword) {
      setLoginError(t('admin.login.required'))
      return
    }
    setLoginLoading(true)
    setLoginError('')
    try {
      const payload = await requestApi<AdminLoginResponse>('/api/v1/admin/auth/login', {
        method: 'POST',
        auth: false,
        body: JSON.stringify({
          username: loginUsername,
          password: loginPassword
        })
      })
      saveAdminToken(payload.token)
      const name = payload.admin?.username || payload.admin?.phone || payload.admin?.email || ''
      setAdminName(name)
      if (payload.admin?.phone) {
        setAdminHint(payload.admin.phone)
      } else if (payload.admin?.email) {
        setAdminHint(payload.admin.email)
      } else {
        setAdminHint('')
      }
      setAccess('granted')
      setLoginPassword('')
    } catch (error) {
      const message = error instanceof Error ? error.message : t('admin.login.failed')
      setLoginError(message)
      setAccess('unauthenticated')
    } finally {
      setLoginLoading(false)
    }
  }

  const handleLogout = () => {
    clearAdminToken()
    setAccess('unauthenticated')
    setAdminName('')
    setAdminHint('')
  }

  const isGranted = access === 'granted'
  const accessValue = useMemo(() => ({ access, isGranted }), [access, isGranted])

  if (!isGranted) {
    return (
      <AdminAccessContext.Provider value={accessValue}>
        <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-6 py-12">
          <div className="w-full max-w-md bg-white border border-slate-200 rounded-3xl shadow-sm p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-primary flex items-center justify-center">
                <Shield fontSize="small" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-900">{t('admin.login.title')}</h1>
                <p className="text-xs text-slate-500 mt-1">{t('admin.login.subtitle')}</p>
              </div>
            </div>

            {access === 'checking' ? (
              <div className="flex flex-col items-center justify-center py-8 text-slate-500">
                <div className="h-10 w-10 rounded-full border-4 border-blue-100 border-t-primary animate-spin mb-4" />
                <p className="text-xs font-medium">{t('admin.login.checking')}</p>
              </div>
            ) : (
              <form onSubmit={handleLoginSubmit} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                    {t('admin.login.username')}
                  </label>
                  <input
                    value={loginUsername}
                    onChange={(event) => setLoginUsername(event.target.value)}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium focus:border-primary focus:ring-4 focus:ring-primary/10"
                    placeholder={t('admin.login.usernamePlaceholder')}
                    autoComplete="username"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                    {t('admin.login.password')}
                  </label>
                  <input
                    value={loginPassword}
                    onChange={(event) => setLoginPassword(event.target.value)}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium focus:border-primary focus:ring-4 focus:ring-primary/10"
                    type="password"
                    placeholder={t('admin.login.passwordPlaceholder')}
                    autoComplete="current-password"
                  />
                </div>
                {loginError && (
                  <div className="text-xs text-rose-500 font-semibold">{loginError}</div>
                )}
                {access === 'denied' && (
                  <div className="text-xs text-rose-500 font-semibold">{t('admin.login.denied')}</div>
                )}
                <button
                  type="submit"
                  disabled={loginLoading}
                  className="w-full py-3 rounded-xl bg-primary text-white text-sm font-bold shadow-lg shadow-blue-500/20 hover:bg-blue-700 transition-colors disabled:opacity-70"
                >
                  {loginLoading ? t('admin.login.loggingIn') : t('admin.login.action')}
                </button>
              </form>
            )}
          </div>
        </div>
      </AdminAccessContext.Provider>
    )
  }

  return (
    <AdminAccessContext.Provider value={accessValue}>
      <div className="min-h-screen bg-[#F8FAFC]">
        <header className="border-b border-slate-200 bg-white sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-primary text-white flex items-center justify-center font-bold">A</div>
              <div>
                <p className="text-sm font-bold text-slate-900">{t('admin.title')}</p>
                <p className="text-[11px] text-slate-400">{t('admin.subtitle')}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {adminName && (
                <div className="text-right">
                  <p className="text-xs font-semibold text-slate-700">{adminName}</p>
                  {adminHint && <p className="text-[10px] text-slate-400">{adminHint}</p>}
                </div>
              )}
              <button
                onClick={() => navigate('/workspace')}
                className="px-3 py-2 text-xs font-semibold border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50"
              >
                {t('admin.backToWorkspace')}
              </button>
              <button
                onClick={handleLogout}
                className="px-3 py-2 text-xs font-semibold border border-slate-200 rounded-lg text-slate-500 hover:bg-slate-50"
              >
                {t('admin.login.logout')}
              </button>
            </div>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-6 py-8 flex gap-6">
          <aside className="w-[240px] shrink-0 hidden lg:block">
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden sticky top-24">
              <nav className="py-4">
                {navItems.map((item) => {
                  const isActive = (() => {
                    if (location.pathname === item.path) {
                      return true
                    }
                    if (item.path === '/admin') {
                      return false
                    }
                    if (!location.pathname.startsWith(item.path)) {
                      return false
                    }
                    const hasMoreSpecificMatch = navItems.some((other) => {
                      if (other.path === item.path) return false
                      if (other.path.length <= item.path.length) return false
                      return location.pathname.startsWith(other.path)
                    })
                    return !hasMoreSpecificMatch
                  })()
                  return (
                    <button
                      key={item.key}
                      onClick={() => navigate(item.path)}
                      className={`w-full flex items-center gap-3 px-6 py-3 text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-blue-50 text-primary border-r-4 border-primary'
                          : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
                      }`}
                    >
                      <span className={isActive ? 'text-primary' : 'text-slate-400'}>{item.icon}</span>
                      {item.label}
                    </button>
                  )
                })}
              </nav>
            </div>
          </aside>

          <main className="flex-1 min-w-0">
            <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-6">
                <div>
                  <h1 className="text-xl font-bold text-slate-900">{title}</h1>
                  {description && <p className="text-sm text-slate-500 mt-1">{description}</p>}
                </div>
                {actions && <div className="flex items-center gap-3">{actions}</div>}
              </div>

              {children}
            </div>
          </main>
        </div>
      </div>
    </AdminAccessContext.Provider>
  )
}
